import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import AgentTenderForm from "../../components/agent-tender/AgentTenderForm";
import VerifyNowModal from "../../components/agent-tender/VerifyNowModal";
import { agentTenderService } from "../../services/agentTenderApi";
import { trustedAgentService } from "../../services/clubNetworkApi";
import {
  formatPositionValue,
  formatStaffRole,
  formatTypeOfTender,
} from "../../constants/agentTenderChoices";

// B2 (Commissioned Search) — map the T3 `prefill` payload (form-field names) onto
// the AgentTenderForm's `initial` shape. Mirrors EditAgentTender.toFormShape: the
// only rename is fix_market_value → market_value_fixed; the commissioning club
// (fully visible on the agent seam) travels as a ClubPicker-shaped value in
// router state. The commission (salary/budget/commission) is NEVER in `prefill`
// by design (A12/B2), so it can never leak into the posted request.
const str = (v) => (v === null || v === undefined ? "" : String(v));
const prefillToInitial = (prefill, club) => {
  const out = { club: club || null };
  if (!prefill) return out;
  [
    "tender_category",
    "position",
    "staff_role",
    "type_of_tender",
    "recruiting_for",
    "currency",
    "market_value_type",
    "description",
  ].forEach((k) => {
    if (prefill[k] !== undefined) out[k] = prefill[k];
  });
  ["min_age", "max_age", "minimum_height", "market_value_from", "market_value_to"].forEach(
    (k) => {
      if (prefill[k] !== undefined) out[k] = str(prefill[k]);
    },
  );
  if (prefill.fix_market_value !== undefined)
    out.market_value_fixed = str(prefill.fix_market_value);
  if (prefill.only_free_players !== undefined)
    out.only_free_players = !!prefill.only_free_players;
  if (prefill.foot !== undefined) out.foot = prefill.foot;
  return out;
};

/* ── Verbatim artboard SVGs (docs 02/03, RULE 2) ──────────────────────────── */

const IcChevronLeft = (props) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M15 18l-6-6 6-6" />
  </svg>
);

const IcCheckBig = (props) => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#047857" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M4 12l5 5L20 7" />
  </svg>
);

const IcShieldCheck = (props) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#047857" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M12 2l7 3v6c0 5-3.5 8.5-7 10-3.5-1.5-7-5-7-10V5z" />
    <path d="M9 12l2 2 4-4" />
  </svg>
);

const IcArrowUp = (props) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#1B6FE0" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M12 19V5M5 12l7-7 7 7" />
  </svg>
);

// The "yours stays first" rank-stack mark — three bars fading out (verbatim,
// incl. the .55/.25 opacities).
const IcRankStack = (props) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#B45309" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <rect x="3" y="5" width="18" height="4" rx="1" />
    <rect x="3" y="11" width="18" height="4" rx="1" opacity=".55" />
    <rect x="3" y="17" width="18" height="4" rx="1" opacity=".25" />
  </svg>
);

// Page header per artboard: 32px bordered back square (verbatim chevron),
// 21px/800 title, Cancel text link right (desktop; the mobile board has no
// header Cancel — the stacked form CTAs carry it).
export const AgentTenderPageHeader = ({ title, onBack, backLabel, onCancel }) => (
  <div className="flex items-center gap-2.5 mb-4">
    <button
      type="button"
      onClick={onBack}
      aria-label={backLabel}
      className="w-8 h-8 flex-none inline-flex items-center justify-center border border-[#E2E8F0] rounded-[9px] bg-white text-[#475569] hover:bg-gray-50"
    >
      <IcChevronLeft />
    </button>
    <h1 className="text-lg sm:text-[21px] font-extrabold tracking-tight text-[#0F172A]">
      {title}
    </h1>
    <span className="flex-1" />
    {onCancel && (
      <button
        type="button"
        onClick={onCancel}
        className="hidden sm:inline text-[13px] font-semibold text-[#64748B] hover:text-gray-900"
      >
        Cancel
      </button>
    )}
  </div>
);

// Post-success step (V2, design doc 03 screen 5): after a successful POST the
// agent chooses "Verify your request" (primary → the existing verify entry,
// VerifyNowModal) or "Do it later" (→ My Requests, where "Verify now" waits on
// the journey row). Replaces the old silent redirect. Benefit rows carry the
// artboard's responsive copy (long lines ≥sm, the 390 board's short lines
// below).
const PostSuccessStep = ({ tender, onVerify, onLater }) => {
  const roleLabel =
    tender?.tender_category === "staff"
      ? formatStaffRole(tender?.staff_role, tender?.other_staff_position)
      : formatPositionValue(tender?.position, tender?.other_staff_position);
  const summary = [
    roleLabel,
    formatTypeOfTender(tender?.type_of_tender),
    formatTypeOfTender(tender?.recruiting_for),
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <div className="max-w-[560px] mx-auto sm:mt-10 bg-white rounded-2xl shadow-[0_24px_64px_rgba(15,23,42,.28)] p-5 sm:p-8 flex flex-col gap-4">
      <div className="flex flex-col items-center gap-2.5 text-center">
        <span className="w-14 h-14 rounded-full bg-[#ECFDF5] inline-flex items-center justify-center">
          <IcCheckBig />
        </span>
        <h2 className="text-[21px] sm:text-[22px] font-extrabold tracking-tight">Request posted</h2>
        {summary && (
          <p className="text-[13px] text-[#64748B]">
            {summary}
            {tender?.club?.name ? ` · for ${tender.club.name}` : ""}
          </p>
        )}
      </div>

      <div className="bg-white sm:bg-[#F8FAFC] border border-[#EEF2F7] rounded-xl px-4 py-3.5 flex flex-col gap-2.5">
        <div className="hidden sm:block text-xs font-extrabold uppercase tracking-[.04em] text-[#475569]">
          What verification gives you
        </div>
        <div className="flex gap-[9px] items-start">
          <IcShieldCheck className="flex-none mt-[1px]" />
          <span className="text-[12.5px] text-[#334155]">
            <span className="hidden sm:inline">
              The <b>Club Verified</b> badge: the club confirms this need and
              you as its channel.
            </span>
            <span className="sm:hidden">
              <b>Club Verified</b> badge on your card
            </span>
          </span>
        </div>
        <div className="flex gap-[9px] items-start">
          <IcArrowUp className="flex-none mt-[1px]" />
          <span className="text-[12.5px] text-[#334155]">
            <span className="hidden sm:inline">
              <b>Shown first:</b> verified requests always rank above unverified
              ones.
            </span>
            <span className="sm:hidden">Shown first in the feed</span>
          </span>
        </div>
        <div className="flex gap-[9px] items-start">
          <IcRankStack className="flex-none mt-[1px]" />
          <span className="text-[12.5px] text-[#334155]">
            <span className="hidden sm:inline">
              <b>Yours stays first:</b> while your request is active, identical
              verified duplicates are superseded.
            </span>
            <span className="sm:hidden">
              Identical duplicates are superseded while yours is active
            </span>
          </span>
        </div>
      </div>

      <button
        type="button"
        onClick={onVerify}
        className="h-12 rounded-[11px] bg-[#2283FF] text-white text-[14px] font-semibold hover:bg-[#1B6FE0]"
      >
        Verify your request
      </button>
      <button
        type="button"
        onClick={onLater}
        className="h-11 rounded-[11px] border border-[#E2E8F0] bg-white text-[#334155] text-[13.5px] font-semibold hover:bg-gray-50"
      >
        Do it later
      </button>
      <p className="text-[11.5px] text-[#94A3B8] text-center">
        <span className="hidden sm:inline">
          Do it later: you can start verification anytime from My Requests.
          Confirming means only that the club recognizes this need and this
          agent as its channel.
        </span>
        <span className="sm:hidden">
          You can start verification anytime from My Requests.
        </span>
      </p>
    </div>
  );
};

const CreateAgentTender = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  // The created record (Mine-serializer shape) — set on success, switches the
  // page from the form to the post-success step.
  const [posted, setPosted] = useState(null);
  const [showVerify, setShowVerify] = useState(false);

  // B2 hand-over context. `?commission=<searchRequestId>` marks a commissioned
  // post; the commissioning club arrives in router state (from the agent inbox
  // B1 card). The locked criteria + verified-from-birth come from the T3 prefill.
  const commissionId = searchParams.get("commission");
  const commissionClub = location.state?.commissionClub || null;
  const [prefill, setPrefill] = useState(null);
  const [prefillErr, setPrefillErr] = useState(null);
  const [loadingPrefill, setLoadingPrefill] = useState(!!commissionId);

  useEffect(() => {
    if (!commissionId) return undefined;
    let cancelled = false;
    setLoadingPrefill(true);
    trustedAgentService.getCommissionPrefill(commissionId).then((res) => {
      if (cancelled) return;
      if (res.success) setPrefill(res.data);
      else setPrefillErr(res.error || "Could not load the commission details.");
      setLoadingPrefill(false);
    });
    return () => {
      cancelled = true;
    };
  }, [commissionId]);

  const handleSubmit = async (payload) => {
    // B2: the commissioned post carries the search-request id — the backend
    // routes it to the verified-from-birth commissioned create and re-enforces
    // the locked criteria server-side (a client can never smuggle a changed one).
    const body = commissionId
      ? { ...payload, search_request_id: prefill?.search_request_id || commissionId }
      : payload;
    const res = await agentTenderService.create(body);
    if (res.success) {
      // A commissioned request is already Club Verified — skip the verify step
      // and go straight to My Requests (the searching state now shows there).
      if (commissionId) {
        navigate("/agent-tenders/mine");
      } else {
        setPosted(res.data);
        window.scrollTo({ top: 0 });
      }
    }
    return res;
  };

  const goToMine = () => navigate("/agent-tenders/mine");
  const goToFeed = () => navigate("/agent-tenders");
  const goBack = () => (commissionId ? navigate("/network/search-requests/inbox") : goToFeed());

  const commissionProps = commissionId
    ? {
        initial: prefillToInitial(prefill?.prefill, commissionClub),
        commission: {
          locked: prefill?.locked_fields || [],
          clubName: commissionClub?.name || "",
        },
      }
    : {};

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Wrap widths per artboard: full-width to 1024 (form centered at 760),
          rail joins ≥1280, wrap capped at 1280px from 1920. */}
      <div className="px-4 md:px-6 lg:px-7 xl:px-8 py-5">
        <div className="mx-auto w-full lg:max-w-[760px] xl:max-w-none min-[1920px]:max-w-[1280px]">
          {posted ? (
            <PostSuccessStep
              tender={posted}
              onVerify={() => setShowVerify(true)}
              onLater={goToMine}
            />
          ) : loadingPrefill ? (
            <div className="flex justify-center py-24 text-[#64748B]">
              Loading the commission…
            </div>
          ) : prefillErr ? (
            <div className="flex flex-col items-center py-24 gap-3 text-center">
              <p className="text-red-500">{prefillErr}</p>
              <button
                onClick={goBack}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                Back to my search requests
              </button>
            </div>
          ) : (
            <>
              <AgentTenderPageHeader
                title={commissionId ? "Post your Search Request" : "Post an agent request"}
                onBack={goBack}
                backLabel={commissionId ? "Back to my search requests" : "Back to feed"}
                onCancel={goBack}
              />
              <AgentTenderForm
                submitLabel={commissionId ? "Post Search Request" : "Post request"}
                onSubmit={handleSubmit}
                onCancel={goBack}
                {...commissionProps}
              />
            </>
          )}
        </div>
      </div>

      {/* Single verify entry (current modal) — the V2 verify surface replaces
          this from the inside; this wiring stays the one CTA path. */}
      <VerifyNowModal
        isOpen={showVerify}
        onClose={() => {
          setShowVerify(false);
          goToMine();
        }}
        tender={posted}
        defaultEmail={posted?.club?.contact_person_email || ""}
      />
    </div>
  );
};

export default CreateAgentTender;
